import React from "react";
import {
  useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import TextField from './SearchableTextField';

import DownloadIcon from "@mui/icons-material/Download";
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DescriptionIcon from "@mui/icons-material/Description";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import PaymentsIcon from "@mui/icons-material/Payments";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import FatturazioneDialog from "./FatturazioneDialog";
import { generateInvoiceXML } from "../APIs/InvoicesAPI";
import { generaHTMLFattura, openPDFtab } from "../utils/openPDFTab";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const DEFAULT_CHIP_STYLE = {
  color: "#555",
  background: "#F2F2F2",
  border: "#CCC",
};

const TYPE_CHIP_STYLES = {
  FATTURA: {
    color: "#125605",
    background: "#D8FFE5",
    border: "#125605",
  },
  DOCUMENTO_COMMERCIALE: {
    color: "#0A4FAF",
    background: "#E8F1FF",
    border: "#0A4FAF",
  },
  CONTO_DIMOSTRATIVO: {
    color: "#A86A00",
    background: "#FFF3D8",
    border: "#A86A00",
  },
  NOTA_CREDITO: {
    color: "#A80000",
    background: "#F8E5E5",
    border: "#A80000",
  },
};

const STATUS_CHIP_STYLES = {
  BOZZA: {
    color: "#A86A00",
    background: "#FFF3D8",
    border: "#A86A00",
  },
  EMESSO: {
    color: "#0A4FAF",
    background: "#E8F1FF",
    border: "#0A4FAF",
  },
  PAGATO: {
    color: "#125605",
    background: "#D8FFE5",
    border: "#125605",
  },
  ANNULLATO: {
    color: "#A80000",
    background: "#F8E5E5",
    border: "#A80000",
  },
  STORNATO: {
    color: "#A80000",
    background: "#F8E5E5",
    border: "#A80000",
  },
  INVIO_IN_CORSO: {
    color: "#0A4FAF",
    background: "#E8F1FF",
    border: "#0A4FAF",
  },
  INVIATA: {
    color: "#125605",
    background: "#D8FFE5",
    border: "#125605",
  },
  ERRORE: {
    color: "#A80000",
    background: "#F8E5E5",
    border: "#A80000",
  },
  ANNULLAMENTO_IN_CORSO: {
    color: "#A86A00",
    background: "#FFF3D8",
    border: "#A86A00",
  },
};

const BILLING_TABLE_COLUMNS = {
  select: { width: "2.5%" },
  documentType: { width: "7.5%" },
  number: { width: "8.5%" },
  date: { width: "6.3%" },
  customer: { width: "8.5%" },
  vatCode: { width: "7%" },
  taxable: { width: "6%" },
  vat: { width: "4.5%" },
  total: { width: "5.5%" },
  paymentMethod: { width: "6%" },
  incassoStatus: { width: "11%" },
  paymentDate: { width: "7%" },
  location: { width: "4.5%" },
  status: { width: "5%" },
  actions: { width: "10%" },
};

const INCASSO_CHIP_STYLES = {
  RISCOSSO: {
    color: "#125605",
    background: "#D8FFE5",
    border: "#125605",
  },
  NON_RISCOSSO: {
    color: "#A80000",
    background: "#F8E5E5",
    border: "#A80000",
  },
  NON_RISCOSSO_POSTICIPATO: {
    color: "#A86A00",
    background: "#FFF3D8",
    border: "#A86A00",
  },
};

const TYPE_LABEL_KEYS = {
  FATTURA: "billing.documentTypes.invoice",
  DOCUMENTO_COMMERCIALE: "billing.documentTypes.commercialDocument",
  CONTO_DIMOSTRATIVO: "billing.documentTypes.draftBill",
  NOTA_CREDITO: "billing.documentTypes.creditNote",
};

const STATUS_LABEL_KEYS = {
  BOZZA: "billing.status.draft",
  EMESSO: "billing.status.issued",
  PAGATO: "billing.status.paid",
  ANNULLATO: "billing.status.cancelled",
  STORNATO: "billing.status.reversed",
  INVIO_IN_CORSO: "billing.status.sending",
  INVIATA: "billing.status.sent",
  ERRORE: "billing.status.error",
  ANNULLAMENTO_IN_CORSO: "billing.status.cancelling",
};

const LOCALE_MAP = {
  it: "it-IT",
  en: "en-GB",
  sq: "sq-AL",
};

const SummaryCard = React.memo(function SummaryCard({
  title,
  value,
  subtitle,
  bg,
  border,
  icon,
  iconColor,
  textColor,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "10px",
        border: `1px solid ${border}`,
        background: bg,
        height: "100%",
        color: textColor,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "inherit" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "30px", fontWeight: 800, mt: 0.5 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "inherit", opacity: 0.8, mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>

        <Box sx={{ color: iconColor }}>{icon}</Box>
      </Box>
    </Paper>
  );
});

function getTypeChipStyle(type) {
  return TYPE_CHIP_STYLES[type] || DEFAULT_CHIP_STYLE;
}

function getStatusChipStyle(status) {
  return STATUS_CHIP_STYLES[status] || DEFAULT_CHIP_STYLE;
}

function getIncassoChipStyle(status) {
  return INCASSO_CHIP_STYLES[status] || DEFAULT_CHIP_STYLE;
}

export default function FatturazioneContent({
  colors,
  summary,
  rows,
  search,
  setSearch,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  formatCurrency,
  formatDate,
  onIncassoStatusChange,
  onPaymentDateChange,
  onLoadInvoicePayments,
  onCreateInvoicePayment,
  onSendSelectedInvoices,
  onExportSelectedInvoices,
  onCancelInvoice,
  onPrintCourtesyCopy,
  onOpenCreateDialog,
  openCreateDialog,
  onCloseCreateDialog,
  onInvoiceCreated,
}) {
  const tenantType = localStorage.getItem("tenantType");
  const { t, i18n } = useTranslation();
  const gt = (key) => getTenantTranslation(t, tenantType, key);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = React.useState([]);
  const [sendingBatch, setSendingBatch] = React.useState(false);
  const [exportingBatch, setExportingBatch] = React.useState(false);
  const [batchResults, setBatchResults] = React.useState([]);
  const [cancelTarget, setCancelTarget] = React.useState(null);
  const [cancellingInvoice, setCancellingInvoice] = React.useState(false);
  const [paymentDialogRow, setPaymentDialogRow] = React.useState(null);
  const [paymentDialogData, setPaymentDialogData] = React.useState(null);
  const [paymentDialogLoading, setPaymentDialogLoading] = React.useState(false);
  const [paymentDialogSubmitting, setPaymentDialogSubmitting] = React.useState(false);
  const [paymentDialogError, setPaymentDialogError] = React.useState("");
  const [paymentForm, setPaymentForm] = React.useState({
    amount: "",
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentMethod: "Bonifico",
    note: "",
  });
  const currentLocale = LOCALE_MAP[i18n.language] || "it-IT";
  const isDark = colors.paper === "#1E1E1E";

  const typeOptions = React.useMemo(() => [
    { value: "ALL", label: t("billing.documentTypes.all") },
    { value: "FATTURA", label: t("billing.documentTypes.invoice") },
    { value: "DOCUMENTO_COMMERCIALE", label: t("billing.documentTypes.commercialDocument") },
    { value: "CONTO_DIMOSTRATIVO", label: t("billing.documentTypes.draftBill") },
    { value: "NOTA_CREDITO", label: t("billing.documentTypes.creditNote") },
  ], [t]);

  const statusOptions = React.useMemo(() => [
    { value: "ALL", label: t("billing.status.all") },
    { value: "BOZZA", label: t("billing.status.draft") },
    { value: "INVIO_IN_CORSO", label: t("billing.status.sending", { defaultValue: "Sending" }) },
    { value: "INVIATA", label: t("billing.status.sent", { defaultValue: "Sent" }) },
    { value: "ERRORE", label: t("billing.status.error", { defaultValue: "Error" }) },
    { value: "EMESSO", label: t("billing.status.issued") },
    { value: "PAGATO", label: t("billing.status.paid") },
    { value: "ANNULLATO", label: t("billing.status.cancelled") },
    { value: "STORNATO", label: t("billing.status.reversed") },
  ], [t]);

  const incassoOptions = React.useMemo(() => [
    { value: "RISCOSSO", label: t("billing.incassoOptions.collected", { defaultValue: "Riscosso" }) },
    { value: "NON_RISCOSSO", label: t("billing.incassoOptions.notCollected", { defaultValue: "Non riscosso" }) },
    {
      value: "NON_RISCOSSO_POSTICIPATO",
      label: t("billing.incassoOptions.postponed", { defaultValue: "Non riscosso con incasso posticipato" }),
    },
  ], [t]);

  const getTypeLabel = React.useCallback((type) => {
    const key = TYPE_LABEL_KEYS[type];
    return key ? t(key) : "-";
  }, [t]);

  const getStatusLabel = React.useCallback((status) => {
    const key = STATUS_LABEL_KEYS[status];
    return key ? t(key) : "-";
  }, [t]);

  const handleReset = () => {
    setSearch("");
    setTypeFilter("ALL");
    setStatusFilter("ALL");
    setDateFrom("");
    setDateTo("");
    setPage(0);
  };

  const canSendInvoice = React.useCallback((row) =>
    Boolean(row?.id)
    && !row.isSent
    && !["INVIO_IN_CORSO", "ANNULLAMENTO_IN_CORSO"].includes(row.status),
  []);

  const canCancelInvoice = React.useCallback((row) =>
    Boolean(row?.id)
    && !["INVIO_IN_CORSO", "ANNULLAMENTO_IN_CORSO"].includes(row.status)
    && !(row.isSent && row.creditNoteId)
    && !(row.type === "NOTA_CREDITO" && row.isSent),
  []); 

  const canManageInvoicePayments = React.useCallback((row) =>
    Boolean(row?.id)
    && row.type !== "NOTA_CREDITO"
    && row.incassoStatus !== "RISCOSSO",
  []);

  const selectableRows = React.useMemo(() => rows.filter((row) => Boolean(row?.id)), [rows]);
  const selectableIds = React.useMemo(() => selectableRows.map((row) => String(row.id)), [selectableRows]);
  const selectedSendableIds = React.useMemo(() => {
    const sendableIds = new Set(rows.filter(canSendInvoice).map((row) => String(row.id)));
    return selectedInvoiceIds.filter((id) => sendableIds.has(String(id)));
  }, [canSendInvoice, rows, selectedInvoiceIds]);
  const selectedOnPage = React.useMemo(
    () => selectedInvoiceIds.filter((id) => selectableIds.includes(String(id))),
    [selectedInvoiceIds, selectableIds]
  );
  const allSelectableSelected = selectableIds.length > 0 && selectedOnPage.length === selectableIds.length;
  const someSelectableSelected = selectedOnPage.length > 0 && selectedOnPage.length < selectableIds.length;

  React.useEffect(() => {
    setSelectedInvoiceIds((prev) => prev.filter((id) => selectableIds.includes(String(id))));
  }, [selectableIds]);

  const toggleSelectAll = (checked) => {
    setSelectedInvoiceIds(checked ? selectableIds : []);
  };

  const toggleSelectInvoice = (id) => {
    const normalizedId = String(id);
    setSelectedInvoiceIds((prev) =>
      prev.includes(normalizedId)
        ? prev.filter((item) => item !== normalizedId)
        : [...prev, normalizedId]
    );
  };

  const handleSendInvoices = async (invoiceIds = selectedSendableIds) => {
    if (!onSendSelectedInvoices || invoiceIds.length === 0) return;
    setSendingBatch(true);
    try {
      const result = await onSendSelectedInvoices(invoiceIds);
      const results = Array.isArray(result?.results) ? result.results : [];
      setBatchResults(results);
      setSelectedInvoiceIds([]);
    } catch (err) {
      setBatchResults([{
        status: "FAILED",
        message: localizedErrorMessage(err, t("billing.batchResults.error", { defaultValue: "Invio non riuscito" })),
      }]);
    } finally {
      setSendingBatch(false);
    }
  };

  const handleExportInvoices = async () => {
    if (!onExportSelectedInvoices || selectedInvoiceIds.length === 0) return;
    setExportingBatch(true);
    try {
      const result = await onExportSelectedInvoices(selectedInvoiceIds);
      const failedIds = Array.isArray(result?.failedIds) ? result.failedIds.map(String) : [];
      setSelectedInvoiceIds(failedIds);
    } finally {
      setExportingBatch(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget || !onCancelInvoice) return;
    setCancellingInvoice(true);
    try {
      await onCancelInvoice(cancelTarget.id);
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== String(cancelTarget.id)));
      setCancelTarget(null);
    } finally {
      setCancellingInvoice(false);
    }
  };

  const paymentDialogTotals = paymentDialogData
    ? {
      total: paymentDialogData?.totals?.total ?? paymentDialogData?.total,
      paid: paymentDialogData?.totals?.paid ?? paymentDialogData?.paid,
      remaining: paymentDialogData?.totals?.remaining ?? paymentDialogData?.remaining,
    }
    : null;

  const openPaymentDialog = async (row) => {
    if (!row) return;
    setPaymentDialogRow(row);
    setPaymentDialogData(null);
    setPaymentDialogError("");
    setPaymentForm({
      amount: Number(row.remainingAmount || row.totalAmount || 0).toFixed(2),
      paymentDate: new Date().toISOString().slice(0, 10),
      paymentMethod: row.paymentMethod && row.paymentMethod !== "-" ? row.paymentMethod : "Bonifico",
      note: "",
    });
    if (!onLoadInvoicePayments) return;
    setPaymentDialogLoading(true);
    try {
      const data = await onLoadInvoicePayments(row.id);
      setPaymentDialogData(data);
      const remaining = Number(data?.totals?.remaining ?? data?.remaining ?? row.remainingAmount ?? row.totalAmount ?? 0);
      setPaymentForm((prev) => ({ ...prev, amount: remaining.toFixed(2) }));
    } catch (err) {
      setPaymentDialogError(err?.message || "Errore caricamento incassi");
    } finally {
      setPaymentDialogLoading(false);
    }
  };

  const closePaymentDialog = (force = false) => {
    if (paymentDialogSubmitting && !force) return;
    setPaymentDialogRow(null);
    setPaymentDialogData(null);
    setPaymentDialogError("");
  };

  const submitPaymentDialog = async () => {
    if (!paymentDialogRow || !onCreateInvoicePayment) return;
    setPaymentDialogSubmitting(true);
    setPaymentDialogError("");
    try {
      await onCreateInvoicePayment(paymentDialogRow.id, {
        amount: Number(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        paymentMethod: paymentForm.paymentMethod,
        note: paymentForm.note,
      });
      closePaymentDialog(true);
    } catch (err) {
      setPaymentDialogError(err?.message || "Errore registrazione incasso");
    } finally {
      setPaymentDialogSubmitting(false);
    }
  };

  const paginatedRows = React.useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [rows.length, rowsPerPage, page]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  async function downloadPDF(invoice_id) {
    const token = localStorage.getItem("accessToken")
    const tenant = localStorage.getItem("tenantId")
    const invoice_xml = await generateInvoiceXML(invoice_id, token, tenant)

    const html = generaHTMLFattura(invoice_xml);

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');

    win.onload = () => {
      win.print();
      URL.revokeObjectURL(url);
    };
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          background: colors.paper,
          border: `1px solid ${colors.border}`,
          borderRadius: "14px",
          overflow: "hidden",
          color: colors.neutralText,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2.5,
            borderBottom: `1px solid ${colors.border}`,
            background: colors.neutralHeaderBg,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "26px", md: "30px" },
                  fontWeight: 800,
                  color: colors.title,
                  lineHeight: 1.1,
                }}
              >
                {t("billing.page")}
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  fontSize: "14px",
                  color: colors.subtitle,
                }}
              >
                {t("billing.subtitle")}
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              flexWrap="wrap"
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onOpenCreateDialog}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  background: colors.green,
                  color: isDark ? "#FFFFFF" : undefined,
                  px: 2.2,
                  "&:hover": {
                    background: "#0D4304",
                  },
                }}
              >
                {t("billing.actions.newDocument")}
              </Button>

              <Button
                variant="contained"
                startIcon={<SendIcon />}
                disabled={selectedSendableIds.length === 0 || sendingBatch || exportingBatch}
                onClick={() => handleSendInvoices()}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  background: colors.gold,
                  color: "#FFFFFF",
                  px: 2.2,
                  "&:hover": {
                    background: colors.goldDark,
                  },
                  "&.Mui-disabled": {
                    color: isDark ? "rgba(255,255,255,0.45)" : undefined,
                  },
                }}
              >
                {sendingBatch
                  ? t("billing.actions.sendingInvoices", { defaultValue: "Invio..." })
                  : t("billing.actions.sendSelectedInvoices", {
                    count: selectedSendableIds.length,
                    defaultValue: `Invia selezionate (${selectedSendableIds.length})`,
                  })}
              </Button>

              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={selectedInvoiceIds.length === 0 || exportingBatch || sendingBatch}
                onClick={handleExportInvoices}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  background: colors.blue,
                  color: isDark ? "#FFFFFF" : undefined,
                  px: 2.2,
                  "&:hover": {
                    background: "#083B82",
                  },
                }}
              >
                {exportingBatch
                  ? t("billing.actions.exporting", { defaultValue: "Esportazione..." })
                  : t("billing.actions.exportSelected", {
                    count: selectedInvoiceIds.length,
                    defaultValue: `Esporta (${selectedInvoiceIds.length})`,
                  })}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SummaryCard
                title={t("billing.summary.totalDocuments")}
                value={summary.totalDocuments}
                subtitle={t("billing.summary.totalDocumentsSubtitle")}
                bg={colors.redBg}
                border={colors.red}
                icon={<ReceiptLongIcon />}
                iconColor={colors.red}
                textColor={colors.neutralText}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <SummaryCard
                title={t("billing.summary.invoices")}
                value={summary.totalInvoices}
                subtitle={t("billing.summary.invoicesSubtitle")}
                bg={colors.greenBg}
                border={colors.green}
                icon={<DescriptionIcon />}
                iconColor={colors.green}
                textColor={colors.neutralText}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <SummaryCard
                title={t("billing.summary.commercialDocuments")}
                value={summary.totalCommercial}
                subtitle={t("billing.summary.commercialDocumentsSubtitle")}
                bg={colors.blueBg}
                border={colors.blue}
                icon={<PointOfSaleIcon />}
                iconColor={colors.blue}
                textColor={colors.neutralText}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <SummaryCard
                title={t("billing.summary.totalAmount")}
                value={formatCurrency(summary.totalAmount, currentLocale)}
                subtitle={t("billing.summary.totalAmountSubtitle")}
                bg={colors.yellowBg}
                border={colors.gold}
                icon={<CreditScoreIcon />}
                iconColor={colors.gold}
                textColor={colors.neutralText}
              />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: "10px",
              border: `1px solid ${colors.border}`,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={12} lg={3}>
                <TextField
                  fullWidth
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("billing.filters.searchPlaceholder")}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  select
                  fullWidth
                  label={t("billing.filters.documentType")}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  {typeOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  select
                  fullWidth
                  label={t("billing.filters.status")}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  {statusOptions.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  label={t("billing.filters.from")}
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <TextField
                  fullWidth
                  label={t("billing.filters.to")}
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={12} lg={1}>
                <Box display="flex" justifyContent={{ xs: "stretch", lg: "flex-end" }}>
                  <Button
                    onClick={handleReset}
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 700,
                      borderColor: colors.gold,
                      color: isDark ? "#FFFFFF" : colors.gold,
                      background: isDark ? "rgba(168,106,0,0.18)" : "transparent",
                      "&:hover": {
                        borderColor: colors.goldDark,
                        color: isDark ? "#FFFFFF" : colors.goldDark,
                        background: isDark ? "rgba(168,106,0,0.3)" : undefined,
                      },
                    }}
                  >
                    {t("billing.filters.reset")}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mt: 3,
              borderRadius: "10px",
              border: `1px solid ${colors.border}`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${colors.border}`,
                background: colors.neutralHeaderBg,
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: colors.neutralText }}>
                  {rows.length} {t("billing.table.recordsFound")}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SendIcon />}
                  disabled={selectedSendableIds.length === 0 || sendingBatch || exportingBatch}
                  onClick={() => handleSendInvoices()}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: "8px",
                    background: colors.gold,
                    color: "#FFFFFF",
                    "&:hover": {
                      background: colors.goldDark,
                    },
                  }}
                >
                  {sendingBatch
                    ? t("billing.actions.sendingInvoices", { defaultValue: "Invio..." })
                    : t("billing.actions.sendSelectedInvoices", {
                      count: selectedSendableIds.length,
                      defaultValue: `Invia selezionate (${selectedSendableIds.length})`,
                    })}
                </Button>
              </Stack>
            </Box>

            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table
                sx={{
                  minWidth: 1760,
                  tableLayout: "fixed",
                  "& th, & td": {
                    px: 0.7,
                    overflowWrap: "normal",
                    wordBreak: "normal",
                    fontSize: "12px",
                  },
                  "& .MuiChip-root": {
                    maxWidth: "100%",
                  },
                  "& .MuiChip-label": {
                    overflow: "visible",
                    textOverflow: "clip",
                    whiteSpace: "normal",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      background: colors.neutralTableHeadBg,
                      "& th": {
                        fontWeight: 800,
                        color: colors.neutralText,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        lineHeight: 1.25,
                        borderBottom: `1px solid ${colors.border}`,
                      },
                    }}
                  >
                    <TableCell padding="checkbox" sx={BILLING_TABLE_COLUMNS.select}>
                      <Checkbox
                        size="small"
                        checked={allSelectableSelected}
                        indeterminate={someSelectableSelected}
                        disabled={selectableIds.length === 0 || sendingBatch || exportingBatch}
                        onChange={(event) => toggleSelectAll(event.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.documentType}>{t("billing.table.columns.documentType")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.number}>{t("billing.table.columns.number")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.date}>{t("billing.table.columns.date")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.customer}>{t("billing.table.columns.customer")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.vatCode}>{t("billing.table.columns.vatOrFiscalCode")}</TableCell>
                    <TableCell align="right" sx={BILLING_TABLE_COLUMNS.taxable}>{t("billing.table.columns.taxableAmount")}</TableCell>
                    <TableCell align="right" sx={BILLING_TABLE_COLUMNS.vat}>{t("billing.table.columns.vat")}</TableCell>
                    <TableCell align="right" sx={BILLING_TABLE_COLUMNS.total}>{t("billing.table.columns.total")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.paymentMethod}>{t("billing.table.columns.paymentMethod")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.incassoStatus}>{gt("billing.table.columns.incassoStatus")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.paymentDate}>{gt("billing.table.columns.paymentDate")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.location}>{t("billing.table.columns.locationTable")}</TableCell>
                    <TableCell sx={BILLING_TABLE_COLUMNS.status}>{t("billing.table.columns.status")}</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...BILLING_TABLE_COLUMNS.actions,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("billing.table.columns.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody
                sx={{background: colors.neutralTableBodyBg}}>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={15} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "16px" }}>
                          {t("billing.empty.title")}
                        </Typography>
                        <Typography sx={{ color: colors.neutralMuted, mt: 0.5 }}>
                          {t("billing.empty.subtitle")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRows.map((row) => {
                      const typeStyle = getTypeChipStyle(row.type);
                      const statusStyle = getStatusChipStyle(row.status);
                      const incassoStyle = getIncassoChipStyle(row.incassoStatus || "NON_RISCOSSO");
                      const rowCanCancel = canCancelInvoice(row);
                      const rowCanManagePayments = canManageInvoicePayments(row);
                      return (
                        <TableRow
                          key={row.sdiIdentifier}
                          hover
                          sx={{
                            "& td": {
                              borderBottom: `1px solid ${colors.rowBorder}`,
                              verticalAlign: "middle",
                              whiteSpace: "normal",
                              wordBreak: "normal",
                              overflowWrap: "break-word",
                            },
                          }}
                        >
                          <TableCell padding="checkbox" sx={BILLING_TABLE_COLUMNS.select}>
                            <Checkbox
                              size="small"
                              checked={selectedInvoiceIds.includes(String(row.id))}
                              disabled={sendingBatch || exportingBatch}
                              onChange={() => toggleSelectInvoice(row.id)}
                            />
                          </TableCell>
                          <TableCell sx={BILLING_TABLE_COLUMNS.documentType}>
                            <Chip
                              label={getTypeLabel(row.type)}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                color: typeStyle.color,
                                background: typeStyle.background,
                                border: `1px solid ${typeStyle.border}`,
                                height: "auto",
                                "& .MuiChip-label": {
                                  display: "block",
                                  whiteSpace: "normal",
                                  py: 0.4,
                                  lineHeight: 1.2,
                                },
                              }}
                            />
                          </TableCell>

                          <TableCell sx={BILLING_TABLE_COLUMNS.number}>
                            <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>{row.number}</Typography>
                            {row.originalInvoiceId ? (
                              <Typography sx={{ fontSize: "12px", color: colors.neutralMuted, mt: 0.3 }}>
                                {t("billing.creditNote.originalInvoice", {
                                  id: row.originalInvoiceNumber || row.originalInvoiceId,
                                  defaultValue: `Storno fattura ${row.originalInvoiceNumber || row.originalInvoiceId}`,
                                })}
                              </Typography>
                            ) : null}
                            {row.creditNoteNumber ? (
                              <Typography sx={{ fontSize: "12px", color: colors.neutralMuted, mt: 0.3 }}>
                                {t("billing.creditNote.creditNote", {
                                  number: row.creditNoteNumber,
                                  defaultValue: `Nota di credito ${row.creditNoteNumber}`,
                                })}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ ...BILLING_TABLE_COLUMNS.date, whiteSpace: "nowrap" }}>
                            {formatDate(row.date, currentLocale)}
                          </TableCell>

                          <TableCell sx={BILLING_TABLE_COLUMNS.customer}>
                            <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>
                              {row.customerName || "-"}
                            </Typography>
                            {row.reason ? (
                              <Typography sx={{ fontSize: "12px", color: colors.neutralMuted, mt: 0.3 }}>
                                {row.reason}
                              </Typography>
                            ) : null} {/* da vedere se mantenere */}
                          </TableCell>

                          <TableCell
                            sx={{
                              ...BILLING_TABLE_COLUMNS.vatCode,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {row.vatOrFiscalCode || "-"}
                          </TableCell>

                          <TableCell align="right" sx={{ ...BILLING_TABLE_COLUMNS.taxable, whiteSpace: "nowrap" }}>
                            {formatCurrency(row.taxableAmount, currentLocale)}
                          </TableCell>

                          <TableCell align="right" sx={{ ...BILLING_TABLE_COLUMNS.vat, whiteSpace: "nowrap" }}>
                            {formatCurrency(row.vatAmount, currentLocale)}
                          </TableCell>

                          <TableCell align="right" sx={{ ...BILLING_TABLE_COLUMNS.total, fontWeight: 800, whiteSpace: "nowrap" }}>
                            {formatCurrency(row.totalAmount, currentLocale)}
                          </TableCell>

                          <TableCell sx={BILLING_TABLE_COLUMNS.paymentMethod}>{row.paymentMethod || "-"}</TableCell>

                          <TableCell sx={BILLING_TABLE_COLUMNS.incassoStatus}>
                            <TextField
                              select
                              size="small"
                              value={row.incassoStatus || "NON_RISCOSSO"}
                              onChange={(e) => onIncassoStatusChange?.(row.id, e.target.value)}
                              sx={{
                                width: 168,
                                "& .MuiOutlinedInput-root": {
                                  minHeight: 34,
                                  borderRadius: "999px",
                                  fontWeight: 800,
                                  color: incassoStyle.color,
                                  background: incassoStyle.background,
                                  fontSize: "12px",
                                  lineHeight: 1.1,
                                  "& fieldset": {
                                    borderColor: incassoStyle.border,
                                  },
                                  "&:hover fieldset": {
                                    borderColor: incassoStyle.border,
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: incassoStyle.border,
                                  },
                                },
                                "& .MuiSelect-select": {
                                  whiteSpace: "normal",
                                  overflow: "visible",
                                  textOverflow: "clip",
                                  py: 0.65,
                                  pl: 1.2,
                                  pr: 3,
                                  lineHeight: 1.15,
                                },
                              }}
                            >
                              {incassoOptions.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                  {item.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>

                          <TableCell sx={BILLING_TABLE_COLUMNS.paymentDate}>
                            {row.incassoStatus === "RISCOSSO" ? (
                              <TextField
                                size="small"
                                type="date"
                                value={row.paymentDate}
                                onChange={(e) => onPaymentDateChange?.(row.id, e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                  width: 128,
                                  "& .MuiOutlinedInput-root": {
                                    height: 34,
                                    borderRadius: "8px",
                                    fontSize: "12px",
                                  },
                                  "& input": {
                                    px: 1,
                                  },
                                }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: "13px", whiteSpace: "nowrap", color: colors.neutralMuted }}>
                                -
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={BILLING_TABLE_COLUMNS.location}>
                            <Stack spacing={0.2}>
                              <Typography sx={{ fontSize: "13px" }}>
                                {row.locationName || "-"}
                              </Typography>
                              {row.tableName ? (
                                <Typography sx={{ fontSize: "12px", color: colors.neutralMuted }}>
                                  {row.tableName}
                                </Typography>
                              ) : null}
                            </Stack>
                          </TableCell>

                          <TableCell sx={{ ...BILLING_TABLE_COLUMNS.status, whiteSpace: "nowrap" }}>
                            <Chip
                              label={getStatusLabel(row.status)}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                color: statusStyle.color,
                                background: statusStyle.background,
                                border: `1px solid ${statusStyle.border}`,
                              }}
                            />
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              ...BILLING_TABLE_COLUMNS.actions,
                              background: colors.neutralTableBodyBg,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.45}
                              justifyContent="center"
                              flexWrap="nowrap"
                              sx={{
                                pr: 0,
                              }}
                            >
                              <Tooltip title={t("billing.actions.view")}>
                                <Button
                                  onClick={() => openPDFtab(row.id)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    minWidth: 30,
                                    width: 30,
                                    height: 30,
                                    p: 0,
                                    borderRadius: "8px",
                                    color: isDark ? "#DCEBFF" : colors.blue,
                                    borderColor: isDark ? "#2F6CC9" : colors.blue,
                                    background: isDark ? "rgba(21,83,171,0.38)" : "transparent",
                                    "&:hover": {
                                      borderColor: isDark ? "#4B86E0" : colors.blue,
                                      background: isDark ? "rgba(21,83,171,0.52)" : "rgba(10,79,175,0.08)",
                                    },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </Button>
                              </Tooltip>

                              <Tooltip title={t("billing.actions.download")}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => { downloadPDF(row.id) }}
                                  sx={{
                                    minWidth: 30,
                                    width: 30,
                                    height: 30,
                                    p: 0,
                                    borderRadius: "8px",
                                    color: isDark ? "#E6FADC" : colors.green,
                                    borderColor: isDark ? "#2C8A1E" : colors.green,
                                    background: isDark ? "rgba(23,103,9,0.35)" : "transparent",
                                    "&:hover": {
                                      borderColor: isDark ? "#42AA30" : colors.green,
                                      background: isDark ? "rgba(23,103,9,0.5)" : "rgba(18,86,5,0.08)",
                                    },
                                  }}
                                >
                                  <DownloadOutlinedIcon fontSize="small" />
                                </Button>
                              </Tooltip>

                              <Tooltip title={t("billing.actions.printCourtesyCopy", { defaultValue: "Stampa copia cortesia" })}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => onPrintCourtesyCopy?.(row.id)}
                                  sx={{
                                    minWidth: 30,
                                    width: 30,
                                    height: 30,
                                    p: 0,
                                    borderRadius: "8px",
                                    color: isDark ? "#FFE7BD" : colors.gold,
                                    borderColor: isDark ? "#C47A00" : colors.gold,
                                    background: isDark ? "rgba(168,106,0,0.26)" : "transparent",
                                    "&:hover": {
                                      borderColor: colors.gold,
                                      background: isDark ? "rgba(168,106,0,0.42)" : "rgba(168,106,0,0.08)",
                                    },
                                  }}
                                >
                                  <ReceiptLongIcon fontSize="small" />
                                </Button>
                              </Tooltip>

                              <Tooltip title={t("billing.actions.managePayments", { defaultValue: "Gestisci incassi" })}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!rowCanManagePayments}
                                  onClick={() => openPaymentDialog(row)}
                                  sx={{
                                    minWidth: 30,
                                    width: 30,
                                    height: 30,
                                    p: 0,
                                    borderRadius: "8px",
                                    color: isDark ? "#E6FADC" : colors.green,
                                    borderColor: isDark ? "#2C8A1E" : colors.green,
                                    background: isDark ? "rgba(18,86,5,0.25)" : "transparent",
                                    "&:hover": {
                                      borderColor: colors.green,
                                      background: isDark ? "rgba(18,86,5,0.38)" : "rgba(18,86,5,0.08)",
                                    },
                                  }}
                                >
                                  <PaymentsIcon fontSize="small" />
                                </Button>
                              </Tooltip>

                              <Tooltip title={t("billing.actions.cancelInvoice", { defaultValue: "Annulla fattura" })}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!rowCanCancel || cancellingInvoice}
                                  onClick={() => setCancelTarget(row)}
                                  sx={{
                                    minWidth: 30,
                                    width: 30,
                                    height: 30,
                                    p: 0,
                                    borderRadius: "8px",
                                    color: isDark ? "#FFDADA" : colors.red,
                                    borderColor: isDark ? "#A80000" : colors.red,
                                    background: isDark ? "rgba(168,0,0,0.22)" : "transparent",
                                    "&:hover": {
                                      borderColor: colors.red,
                                      background: isDark ? "rgba(168,0,0,0.35)" : "rgba(168,0,0,0.08)",
                                    },
                                  }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>

            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={t("common.pagination.rowsPerPage")}
              labelDisplayedRows={({ from, to, count }) =>
                t("common.pagination.displayedRows", { from, to, count })
              }
              sx={{
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  rowGap: 1,
                  justifyContent: { xs: "flex-start", sm: "space-between" },
                  color: colors.neutralText,
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  color: colors.neutralText,
                },
                "& .MuiTablePagination-actions .MuiIconButton-root": {
                  color: colors.neutralText,
                },
              }}
            />
          </Paper>
        </Box>
      </Paper>

      <FatturazioneDialog
        openCreateDialog={openCreateDialog}
        onCloseCreateDialog={onCloseCreateDialog}
        onInvoiceCreated={onInvoiceCreated}
        colors={colors}
        isDark={isDark}
      />

      <Dialog open={Boolean(paymentDialogRow)} onClose={closePaymentDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("billing.payments.title", { defaultValue: "Incassi fattura" })}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {paymentDialogRow ? (
              <Paper elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: "8px", p: 1.5 }}>
                <Typography sx={{ fontWeight: 800 }}>{paymentDialogRow.number}</Typography>
                <Typography sx={{ fontSize: 13, color: colors.neutralMuted }}>
                  {paymentDialogRow.customerName || "-"}
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: 13 }}>
                    Totale: <strong>{formatCurrency(paymentDialogTotals?.total ?? paymentDialogRow.totalAmount, currentLocale)}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Incassato: <strong>{formatCurrency(paymentDialogTotals?.paid ?? paymentDialogRow.paidAmount, currentLocale)}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    Residuo: <strong>{formatCurrency(paymentDialogTotals?.remaining ?? paymentDialogRow.remainingAmount, currentLocale)}</strong>
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            {paymentDialogError ? (
              <Typography sx={{ color: colors.red, fontWeight: 700, fontSize: 13 }}>
                {paymentDialogError}
              </Typography>
            ) : null}

            {paymentDialogLoading ? (
              <Typography sx={{ fontSize: 13, color: colors.neutralMuted }}>
                {t("common.loading", { defaultValue: "Caricamento..." })}
              </Typography>
            ) : (
              <Stack spacing={1}>
                <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                  {t("billing.payments.history", { defaultValue: "Incassi registrati" })}
                </Typography>
                {(paymentDialogData?.payments || paymentDialogRow?.invoicePayments || []).length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: colors.neutralMuted }}>
                    {t("billing.payments.empty", { defaultValue: "Nessun incasso registrato." })}
                  </Typography>
                ) : (
                  (paymentDialogData?.payments || paymentDialogRow?.invoicePayments || []).map((payment) => (
                    <Stack
                      key={payment.id}
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ borderBottom: `1px solid ${colors.border}`, py: 0.75 }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {formatCurrency(payment.amount, currentLocale)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: colors.neutralMuted }}>
                          {payment.paymentMethod || "-"}{payment.note ? ` - ${payment.note}` : ""}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13 }}>
                        {formatDate(payment.paymentDate, currentLocale)}
                      </Typography>
                    </Stack>
                  ))
                )}
              </Stack>
            )}

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t("billing.payments.amount", { defaultValue: "Importo" })}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))}
                  inputProps={{ min: 0, step: "0.01" }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label={t("billing.payments.date", { defaultValue: "Data" })}
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("billing.payments.method", { defaultValue: "Metodo" })}
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  {["Bonifico", "Contanti", "Carta", "Assegno", "Altro"].map((method) => (
                    <MenuItem key={method} value={method}>{method}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label={t("billing.payments.note", { defaultValue: "Nota" })}
                  value={paymentForm.note}
                  onChange={(e) => setPaymentForm((prev) => ({ ...prev, note: e.target.value }))}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentDialog} disabled={paymentDialogSubmitting}>
            {t("common.cancel", { defaultValue: "Annulla" })}
          </Button>
          <Button
            variant="contained"
            onClick={submitPaymentDialog}
            disabled={paymentDialogSubmitting || !Number(paymentForm.amount)}
          >
            {t("billing.payments.submit", { defaultValue: "Registra incasso" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={batchResults.length > 0} onClose={() => setBatchResults([])} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("billing.batchResults.title", { defaultValue: "Risultato invio fatture" })}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            {batchResults.map((result, index) => {
              const status = String(result?.status || "-");
              const color = status === "SENT" ? "success" : status === "FAILED" ? "error" : "warning";
              return (
                <Stack
                  key={`${result?.invoiceId || "batch"}-${index}`}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  justifyContent="space-between"
                  sx={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: "8px",
                    p: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "13px" }}>
                      {result?.invoiceId
                        ? t("billing.batchResults.invoice", {
                          id: result.invoiceId,
                          defaultValue: `Fattura #${result.invoiceId}`,
                        })
                        : t("billing.batchResults.generic", { defaultValue: "Invio" })}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: colors.neutralMuted }}>
                      {result?.message || "-"}
                    </Typography>
                  </Box>
                  <Chip size="small" color={color} label={status} sx={{ fontWeight: 800 }} />
                </Stack>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchResults([])}>
            {t("common.actions.close", { defaultValue: "Chiudi" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(cancelTarget)} onClose={() => !cancellingInvoice && setCancelTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {t("billing.cancelDialog.title", { defaultValue: "Annulla fattura" })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontWeight: 800, mb: 1 }}>
            {cancelTarget?.number || "-"}
          </Typography>
          <Typography sx={{ color: colors.neutralText }}>
            {cancelTarget?.isSent
              ? t("billing.cancelDialog.sentMessage", { defaultValue: "La fattura e gia stata inviata. Verra creata una nota di credito in bozza." })
              : t("billing.cancelDialog.draftMessage", { defaultValue: "Questa bozza verra eliminata." })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={cancellingInvoice}>
            {t("billing.createDialog.actions.cancel")}
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={cancellingInvoice}>
            {cancellingInvoice
              ? t("billing.cancelDialog.cancelling", { defaultValue: "Annullamento..." })
              : t("billing.cancelDialog.confirm", { defaultValue: "Conferma" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
